import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { NotePencil, TrendUp, TrendDown, Briefcase } from "@phosphor-icons/react";

const zoneColor = { green: "#00e676", yellow: "#ffea00", red: "#ff3b30" };

export default function JournalTab() {
  const [journal, setJournal] = useState(null);
  const [prod, setProd] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([api.get("/health/journal"), api.get("/health/productivity")]).then(([j, p]) => {
      if (j.status === "fulfilled") setJournal(j.value.data);
      if (p.status === "fulfilled") setProd(p.value.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="p-8 text-center text-sm text-muted-foreground" data-testid="journal-loading">Анализирую влияние привычек…</div>;

  const items = journal?.items || [];
  const zones = prod?.zones || [];
  const maxTasks = Math.max(...zones.map((z) => z.avg_tasks), 0.1);

  return (
    <div className="space-y-4" data-testid="journal-tab">
      <div className="glass-card p-5">
        <div className="flex items-center gap-2 mb-1">
          <NotePencil size={18} weight="fill" className="text-[#a970ff]" />
          <h3 className="text-sm font-semibold">Журнал: как привычки влияют на восстановление</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Сравниваем ваше восстановление наутро после дней, когда привычка выполнена, и когда нет. Отмечайте привычки ежедневно — чем больше данных, тем точнее выводы.
        </p>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="journal-empty">
            Пока недостаточно данных: нужно минимум 5 дней с выполнением привычки и 5 без. Продолжайте отмечать привычки — анализ появится автоматически.
          </p>
        ) : (
          <div className="space-y-2">
            {items.map((it) => {
              const good = it.rec_delta >= 0;
              const Arrow = good ? TrendUp : TrendDown;
              return (
                <div key={it.habit} className="rounded-md bg-secondary/40 border border-border/40 p-3.5" data-testid={`journal-item-${it.habit}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{it.habit}</span>
                    <span className={`ml-auto flex items-center gap-1 num font-bold text-sm ${good ? "text-[#00e676]" : "text-[#ff3b30]"}`}>
                      <Arrow size={14} weight="bold" />{good ? "+" : ""}{it.rec_delta} п.п. восстановления
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 num">
                    С привычкой: {it.rec_with}% ({it.n_with} дн) · Без: {it.rec_without}% ({it.n_without} дн)
                    {it.sleep_delta != null && <> · Сон {it.sleep_delta > 0 ? "+" : ""}{Math.round(it.sleep_delta * 60)} мин</>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="glass-card p-5">
        <div className="flex items-center gap-2 mb-1">
          <Briefcase size={18} weight="fill" className="text-[#0084ff]" />
          <h3 className="text-sm font-semibold">Продуктивность × восстановление</h3>
        </div>
        {zones.length < 2 ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="productivity-empty">
            Недостаточно данных — выполняйте задачи в Планнере и отмечайте привычки, чтобы увидеть связь с зонами восстановления.
          </p>
        ) : (
          <>
            {prod.statement && <p className="text-xs mb-4"><span className="text-[#00e676] font-semibold">{prod.statement}</span></p>}
            <div className="space-y-3" data-testid="productivity-zones">
              {zones.map((z) => (
                <div key={z.zone}>
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: zoneColor[z.zone] }} className="font-semibold">{z.label} · {z.days} дн</span>
                    <span className="num text-muted-foreground">{z.avg_tasks} задач/день · {z.avg_habits} привычек/день</span>
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(z.avg_tasks / maxTasks) * 100}%`, background: zoneColor[z.zone] }} />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
